import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthGuard } from '../../../../hooks/useAuthGuard';
import {
  Layout,
  Typography,
  Select,
  Button,
  Card,
  List,
  Flex,
  theme,
  Grid,
  ColorPicker,
  message,
  Input,
} from 'antd';
import { HomeOutlined, SendOutlined, RightOutlined, PlayCircleOutlined, LeftOutlined } from '@ant-design/icons';
import type { ColorPickerProps } from 'antd/es/color-picker';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { useAccount } from '../../../../store';
import { AppearanceState } from '../../types';
import { getAppearanceStateFromAssistant, toColorString } from '../../utils';
import { assistantService } from '../../../assistant-management/services/assistantManagementService';
import { INBOX_MESSAGES } from '../../constants';
import { Chatbot } from '../../../../Components/Chatbot/Chatbot';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text, Link } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

export function Appearance(): React.ReactElement {
  useAuthGuard(); // Proteger esta página con autenticación
  
  const navigate = useNavigate();
  const { currentAccount } = useAccount();
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { currentAssistant, setAssistant } = useAssistant();
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);

  const [appearanceState, setAppearanceState] = useState<AppearanceState>({
    primaryColor: (currentAssistant as any)?.webchatConfig?.colorTitle || '#E3F571',
    headerColor: (currentAssistant as any)?.webchatConfig?.colorMain || '#7E70B3',
    greetingMessage: (currentAssistant as any)?.webchatConfig?.greeting || 'Hello! How can I help you today?',
    iconColor: (currentAssistant as any)?.webchatConfig?.iconBgColor || '#000000'
  });

  // Update appearance state when currentAssistant changes
  useEffect(() => {
    if ((currentAssistant as any)?.webchatConfig) {
      logger.info('Reading assistant data:', {
        assistantName: currentAssistant?.name,
        assistantId: currentAssistant?.id,
        fullAssistant: currentAssistant,
        colorTitle: (currentAssistant as any).webchatConfig.colorTitle,
        colorMain: (currentAssistant as any).webchatConfig.colorMain,
        greeting: (currentAssistant as any).webchatConfig.greeting,
        iconBgColor: (currentAssistant as any).webchatConfig.iconBgColor
      });
      
      setAppearanceState({
        primaryColor: (currentAssistant as any).webchatConfig.colorTitle,
        headerColor: (currentAssistant as any).webchatConfig.colorMain,
        greetingMessage: (currentAssistant as any).webchatConfig.greeting,
        iconColor: (currentAssistant as any).webchatConfig.iconBgColor || '#000000'
      });
    }
  }, [currentAssistant]);

  // Default cloud WebSocket URL
  const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_WEB_CHATBOT;

  const handleSave = async () => {
    if (!currentAssistant) {
      message.error('No assistant selected');
      return;
    }

    try {
      const appearanceData = {
        appearanceState: {
          primaryColor: toColorString(appearanceState.primaryColor),
          headerColor: toColorString(appearanceState.headerColor),
          backgroundColor: toColorString(appearanceState.primaryColor), // Default to primary if not set
          textColor: '#FFFFFF', // Default text color
          buttonColor: toColorString(appearanceState.primaryColor), // Default to primary if not set
          backgroundMessageColor: toColorString(appearanceState.headerColor), // Use header color for messages
          iconColor: toColorString(appearanceState.iconColor),
          greetingMessage: appearanceState.greetingMessage
        },
        currentAssistant: {
          id: currentAssistant.id,
          name: currentAssistant.name,
          businessName: (currentAssistant as any).businessName || '',
          communicationStyle: (currentAssistant as any).communicationStyle || '',
          domainTopic: (currentAssistant as any).domainTopic || '',
          specialist: (currentAssistant as any).specialist || '',
          fallbackResponse: (currentAssistant as any).fallbackResponse || '',
          webchatConfig: (currentAssistant as any).webchatConfig || {}
        },
      };

      logger.info('Saving appearance data:', appearanceData);
      const response = await assistantService.handleSave(appearanceData);
      logger.info('Save response:', response);

      // Handle different response structures
      const updatedAssistant = response?.data?.updatedChatbotData || response?.data;

      if (updatedAssistant && (updatedAssistant.webchatConfig || updatedAssistant.id)) {
        logger.info('Updated assistant data:', updatedAssistant);

        // Update local state if webchatConfig exists
        if (updatedAssistant.webchatConfig) {
          setAppearanceState({
            primaryColor: updatedAssistant.webchatConfig.colorTitle,
            headerColor: updatedAssistant.webchatConfig.colorMain,
            greetingMessage: updatedAssistant.webchatConfig.greeting,
            iconColor: updatedAssistant.webchatConfig.iconBgColor || '#000000'
          });
        }

        // Refresh the assistant context to get the latest data
        await setAssistant(currentAssistant.id);

        message.success('Appearance settings saved successfully');
      } else {
        // If we can't find the expected data structure, still refresh and show success
        // since the server responded successfully
        logger.info('Unexpected response structure, but request was successful');
        await setAssistant(currentAssistant.id);
        message.success('Appearance settings saved successfully');
      }
    } catch (error) {
      logger.error('Error saving appearance:', error);
      message.error('Error saving appearance settings');
    }
  };

  // Define color settings with labels and state
  const colorSettings = [
    {
      label: 'Primary Color',
      value: appearanceState.primaryColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, primaryColor: value }));
        if ((currentAssistant as any)?.webchatConfig) {
          (currentAssistant as any).webchatConfig.colorMain = toColorString(value);
        }
      },
    },
    {
      label: 'Secondary Color',
      value: appearanceState.headerColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, headerColor: value }));
        if ((currentAssistant as any)?.webchatConfig) {
          (currentAssistant as any).webchatConfig.colorTitle = toColorString(value);
        }
      },
    },
    {
      label: 'Icon Color',
      value: appearanceState.iconColor,
      onChange: (value: ColorPickerProps['value']) => {
        setAppearanceState(prev => ({ ...prev, iconColor: value }));
      },
    },
  ];

  return (
    <Card>
    <Layout
      style={{
        background: 'transparent',
        height: '100%',
      }}
    >
      <Content
        style={{
          padding: isMobile ? token.paddingSM : 0,
          position: 'relative',
          height: 'fit-content',
          minHeight: '700px'
        }}
      >
        <Flex
          justify="space-between"
          align="flex-start"
          style={{ marginBottom: token.margin }}
          vertical={isMobile}
          gap={isMobile ? token.marginSM : 0}
        >
          <Title level={isMobile ? 2 : 4} style={{ margin: 0 }}>
            Appearance
          </Title>
          <Button 
            type="primary" 
            style={{ width: isMobile ? '100%' : 'auto' }} 
            onClick={handleSave} 
            icon={<PlayCircleOutlined />}
            id="appearance-save-publish-button"
          >
            Save and publish
          </Button>
        </Flex>

        <Flex gap={token.marginMD} style={{ width: '100%', padding:'0px', height: '100%' }} vertical={isMobile}>
          {/* Opciones de configuración */}
          <Flex
            vertical
            gap={token.marginSM}
            style={{
              width: '100%',
            }}
          >
            {colorSettings.map((setting, index) => (
              <Flex key={index} align="center" gap={token.marginSM} style={{ width: '100%' }}>
                <Text style={{ width: 200, flexShrink: 0 }}>{setting.label}:</Text>
                <ColorPicker
                  value={setting.value}
                  onChange={(color) => setting.onChange(color)}
                  showText
                  style={{ flexGrow: 0 }}
                />
              </Flex>
            ))}
            
            {/* Greeting Message Input */}
            <Flex align="center" gap={token.marginSM} style={{ width: '50%' }}>
              <Text style={{ flexShrink: 0 }}>Greeting Message:</Text>
              <Input.TextArea
                value={appearanceState.greetingMessage}
                onChange={(e) => {
                  setAppearanceState(prev => ({ ...prev, greetingMessage: e.target.value }));
                  if ((currentAssistant as any)?.webchatConfig) {
                    (currentAssistant as any).webchatConfig.greeting = e.target.value;
                  }
                }}
                placeholder="Enter your greeting message"
                rows={7}
                style={{ flexGrow: 1 }}
              />
            </Flex>
          </Flex>
        </Flex>

        {/* Chatbot embebido posicionado absolutamente en el Content */}
        <div
          style={{
            position: isMobile ? 'relative' : 'absolute',
            bottom: isMobile ? 'auto' : '20px',
            right: isMobile ? 'auto' : '20px',
            zIndex: 1000,
            marginTop: isMobile ? token.marginMD : 0,
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-end',
          }}
        >
          <Chatbot
            wsUrl={currentAssistant?.websocketUrl || DEFAULT_WEBSOCKET_URL}
            chatbotId={currentAssistant?.id || '0'}
            channelId="preview"
            urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
            isWeb={false}
            isPreview={true}
            primaryColor={toColorString(appearanceState.primaryColor)}
            headerColor={toColorString(appearanceState.headerColor)}
            iconColor={toColorString(appearanceState.iconColor)}
            botName={currentAssistant?.name}
          />
        </div>
      </Content>
    </Layout>
    </Card>
  );
}
